// @flow

import { logToSentry, LOGGER_COMMON_LOGGING } from './sentryBridge';

/**
 * Common logging functionality. By default, shows info level and up in dev
 * and nothing in production. Log level can be changed at will.
 *
 * @author skline
 * @since 2015-10-15
 */

// eslint-disable-next-line no-nested-ternary
let VERBOSITY = DEBUG ? 4 : SERVER ? 3 : 1;

type JSRecordLogLevel = 'ERROR' | 'WARN';

/** TODO: Remove this and references to this function when Sentry is productionalized */
const recordToJSRecord = (logLevel: JSRecordLogLevel, ...args: any) => {
  if (!SERVER) {
    // Suppress console methods when we record, since it'll be logged later here
    const originalConsoleError = console.error;

    try {
      // $FlowFixMe: Need to overwrite this so we don't double-log
      console.error = () => {};
      /* eslint-disable no-restricted-globals */
      if (window && window.require) {
        const TAError = window.require('ta/util/Error');
        if (TAError && typeof TAError === 'object' && typeof TAError.record === 'function') {
          TAError.record([...args].join(' '), null, null, {}, logLevel);
        }
      }
      /* eslint-enable no-restricted-globals */
    } catch (error) {
      // Do nothing. Used only to control program flow
    } finally {
      // If JSErrorRecord throws an exception, make sure console.error is always preserved
      // $FlowFixMe
      console.error = originalConsoleError;
    }
  }
};

export default {
  error: (...args: any) => {
    recordToJSRecord('ERROR', ...args);
    logToSentry({ error: new Error(args.join(' ')), tags: { logger: LOGGER_COMMON_LOGGING } });
    if (VERBOSITY < 1) return;
    console.error(...args);
  },
  warn: (...args: any) => {
    recordToJSRecord('WARN', ...args);
    if (VERBOSITY < 2) return;
    console.warn(...args);
  },
  dir: (...args: any) => {
    if (VERBOSITY < 3) return;
    console.dir(...args);
  },
  info: (...args: any) => {
    if (VERBOSITY < 3) return;
    console.info(...args);
  },
  log: (...args: any) => {
    if (VERBOSITY < 3) return;
    console.info(...args);
  },
  debug: (...args: any) => {
    if (VERBOSITY < 4) return;
    console.info(...args);
  },
  setLevel: (newLevel: number) => {
    console.info('Set logging level to ', VERBOSITY);
    VERBOSITY = newLevel;
  },
};

export { defineSentry, logToSentry } from './sentryBridge';
